import React, { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react';
import Services from 'src/services/Services';
import { SplashScreen } from 'src/components/loading-screen';
import { getAppConfig } from 'src/services/service-handlers/PrivateService';
import { Box } from '@mui/material';

interface ConfigContextType {
  configData: Record<string, any>;
  isConfigLoaded: boolean;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export const ConfigProvider = ({ children }: { children: ReactNode }) => {
  const [configData, setConfigData] = useState<Record<string, any>>({});
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);

  useEffect(() => {
    const fetchConfigData = async () => {
      try {
        const response = await getAppConfig();
        if (response) {
          setConfigData(response);
        }
      } catch (error) {
        console.error('Error fetching config data:', error);
      } finally {
        setIsConfigLoaded(true);
      }
    };

    fetchConfigData();
  }, []);

  // Use useMemo to optimize context value
  const contextValue = useMemo(
    () => ({
      configData,
      isConfigLoaded,
    }),
    [configData, isConfigLoaded]
  );

  // Show loading screen while config is being loaded
  if (!isConfigLoaded) {
    return <SplashScreen />;
  }

  return <ConfigContext.Provider value={contextValue}>{children}</ConfigContext.Provider>;
};

export const useConfig = (): ConfigContextType => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};
